// Pure helpers for the assistant chat (no React), so they can be unit-tested.

export const HISTORY_LIMIT = 20;
export const STORED_LIMIT = 40;

export const firstNameOf = (fullName) => String(fullName || "").trim().split(/\s+/)[0] || "there";

/** Plain-text form of a task draft: used for Copy and for the model's history. */
export const draftToText = (draft) => {
  if (!draft) return "";
  const body = draft.taskType === "feat" ? ["User Story", draft.userStory] : ["Description", draft.description];
  return [
    `${draft.taskType}: ${draft.title}`,
    `${body[0]}: ${body[1] || ""}`,
    "Acceptance Criteria:",
    ...(draft.acceptanceCriteria || []).map((c) => `- ${c}`),
  ].join("\n");
};

/** Text of any message as the user would copy it. */
export const messageText = (m) => (m.kind === "draft" ? draftToText(m.draft) : m.content || "");

/** The conversation sent to the backend (it keeps only the recent part). */
export const buildHistory = (messages) =>
  messages
    .filter((m) => m.kind !== "error")
    .map((m) => ({ role: m.role, content: m.role === "assistant" ? messageText(m) : m.content }))
    .filter((m) => m.content)
    .slice(-HISTORY_LIMIT);

/** The draft the next message may modify: the newest one still waiting for confirmation. */
export const activeDraft = (messages) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.kind === "draft" && m.status === "pending") return m;
  }
  return null;
};

/** A new draft replaces older pending ones, which can no longer be confirmed. */
export const supersedePending = (messages) =>
  messages.map((m) => (m.kind === "draft" && m.status === "pending" ? { ...m, status: "superseded" } : m));

/** Restore saved messages: an interrupted "creating" goes back to "pending". */
export const restoreMessages = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((m) => m && (m.role === "user" || m.role === "assistant"))
    .map((m) => (m.status === "creating" ? { ...m, status: "pending" } : m))
    .slice(-STORED_LIMIT);
};

/** Split an answer into lines; "-", "*", "•" and "1." lines are bullets. */
export const parseBlocks = (text) =>
  String(text || "")
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const bullet = /^[-*•]\s+/.test(l) || /^\d+[.)]\s+/.test(l);
      return { bullet, text: l.replace(/^[-*•]\s+/, "").replace(/^\d+[.)]\s+/, "").replace(/^#+\s*/, "") };
    });

/** **bold** and `code` runs; everything else stays plain text (never HTML). */
export const parseInline = (text) => {
  const out = [];
  const re = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ type: "text", value: text.slice(last, m.index) });
    out.push(m[1] !== undefined ? { type: "bold", value: m[1] } : { type: "code", value: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: "text", value: text.slice(last) });
  return out;
};
